import { PlanCurrencies, PlanFrequencies, StepKey } from 'api/authentication/requests';

import { request } from '../request';

export type EmailSettings = {
  domain: string;
  fromAddress: string;
  fromName: string;
  isManaged: boolean;
  isVerified: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword?: string;
  dnsRecords?: {
    name: string;
    valid: 'unknown' | 'valid' | 'invalid';
    value: string;
    recordType: 'TXT' | 'MX' | 'CNAME';
  }[];
};

export type Company = {
  name: string;
  smsSenderName: string;
  timezone: string;
  slug: string;
  websiteUrl?: string;
  logo?: string;
  portalLogo?: string;
  portalBanner?: string;
  hasPortalDomain: boolean;
  portalName: string;
  portalFavicon?: string;
  hasCustomPortalDomain: boolean;
  employeeCount: string;
  industry: string;
  hasFinishedPreboarding: boolean;
};

export const loadCompanyDetails = async () => {
  const response = await request.get<Company>(`v1/company/`);
  return response.data;
};

export const editCompanyDetails = async (data: FormData | Partial<Company>) => {
  const response = await request.patch('v1/company/', data);
  return response.data;
};

export const completePreboarding = async () => {
  const response = await request.post('v1/signup/preboarding/');
  return response.data;
};

export const skipOnboarding = async () => {
  const response = await request.patch('v1/company/onboarding/', { isSkipped: true });
  return response.data;
};

export const completeOnboarding = async () => {
  const response = await request.patch('v1/company/onboarding/', { isCompleted: true });
  return response.data;
};

export const completeOnboardingStep = async (step: StepKey) => {
  const response = await request.post('v1/company/onboarding/complete-step/', { [step]: true });
  return response.data;
};

export const loadEmailSettings = async () => {
  const response = await request.get<EmailSettings>(`v1/company/email-settings/`);
  return response.data;
};

export const editEmailSettings = async (data: EmailSettings) => {
  const response = await request.patch('v1/company/email-settings/', data);
  return response.data;
};

export const deleteEmailSettings = async () => {
  const response = await request.delete('v1/company/email-settings/');
  return response.data;
};

export const loadTimezones = async () => {
  const response = await request.get('v1/company/timezones/');
  return response.data;
};

type VerifyEmailSettingsData = {
  smtpHost: string;
  smtpPort: number;
  smtpLogin: string;
  smtpPassword: string;
};

type VerifiedResponse = {
  isVerified: boolean;
};

export const verifyEmailSettings = async (data: VerifyEmailSettingsData) => {
  const response = await request.post<VerifiedResponse>('v1/company/email-settings/verify/', data);
  return response.data;
};

export type AzureADSettings = {
  tenantId: string;
  clientId: string;
};

export const loadAzureADSettingsForEmail = async (email?: string) => {
  const response = await request.get<AzureADSettings>(`v1/company/azure-ad/${email}/`);
  return response.data;
};

export const loadAzureADSettings = async () => {
  const response = await request.get<AzureADSettings>('v1/company/azure-ad/');
  return response.data;
};

export const addAzureADSettings = async (data: AzureADSettings) => {
  const response = await request.post<AzureADSettings>('v1/company/azure-ad/', data);
  return response.data;
};

export const removeAzureADSettings = async () => {
  const response = await request.delete('v1/company/azure-ad/');
  return response.data;
};

export type BillingDetails = {
  card: BillingCard;
  billingAddress: BillingAddress;
  paymentMethod: 'bank_transfer' | 'card';
};

export type BillingCard = {
  cardType: string;
  last4: string;
  expMonth: number;
  expYear: number;
  status: 'valid' | 'expiring' | 'expired';
};

export type BillingAddress = {
  company: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  ssn?: string;
};

export type Invoice = {
  id: string;
  date: string;
  amount: number;
  status: 'paid' | 'posted' | 'payment_due' | 'not_paid' | 'voided' | 'pending' | 'sent';
};

export const loadBillingDetails = async () => {
  const response = await request.get<BillingDetails>('v1/company/billing/details/');
  return response.data;
};

export const editBillingDetails = async (data: BillingAddress) => {
  const response = await request.post('v1/company/billing/details/', data);
  return response.data;
};

export const loadInvoices = async () => {
  const response = await request.get<Invoice[]>('v1/company/billing/invoices/');
  return response.data;
};

export type InvoicePdf = {
  url: string;
};

export const loadInvoicePdf = async (invoiceId: string) => {
  const response = await request.get<InvoicePdf>(`v1/company/billing/invoices/${invoiceId}/`);
  return response.data;
};

export type PaymentMethodPage = {
  url: string;
};

export const loadChargebeePaymentMethodPage = async () => {
  const response = await request.get<PaymentMethodPage>('v1/company/update-payment-method/');
  return response.data;
};

export type planPageData = {
  planType: string;
  currency: PlanCurrencies;
  frequency: PlanFrequencies;
};

export type planPageUrl = {
  url: string;
};

export const loadChargebeePlanPages = async (data: planPageData) => {
  const response = await request.post<planPageUrl>('v1/company/plan-page/', data);
  return response.data;
};
